/**
 * iter45 — Extracted from LeagueDashboard.jsx.
 *
 * Two small modals: AddEmailModal (used by the recovery-email banner) and
 * LogoutConfirmModal (warns the user to save their invite code + username
 * before signing out). Co-located here because both are tiny + only
 * referenced by the dashboard.
 */
import { useState } from "react";
import { Check, Copy, LogOut, X } from "lucide-react";


export function AddEmailModal({ open, onClose, onSave, onToggleNotifications, me, league }) {
    const [draft, setDraft] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [notifBusy, setNotifBusy] = useState(false);
    if (!open) return null;
    const notifsOn = me?.email_notifications_enabled !== false;
    const handleToggleNotifs = async () => {
        if (notifBusy) return;
        setNotifBusy(true);
        try {
            await onToggleNotifications?.(!notifsOn);
        } finally {
            setNotifBusy(false);
        }
    };
    const value = draft ?? (me?.email || "");
    const copy = (key, text) => {
        try { navigator.clipboard?.writeText(text)?.catch?.(() => {}); } catch { /* clipboard denied — non-fatal */ }
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 1500);
    };
    const copyables = [
        me?.username && { key: "username", label: "Username", value: me.username },
        league?.invite_code && (me?.is_admin || me?.is_co_admin) && { key: "invite", label: "Invite code", value: league.invite_code },
        me?.personal_code && { key: "personal", label: "Personal code", value: me.personal_code },
    ].filter(Boolean);
    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="brutal-card p-6 max-w-md w-full"
                data-testid="email-modal"
            >
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-extrabold text-lg uppercase">Your Account</h3>
                    <button onClick={onClose} className="brutal-btn-ghost p-2" aria-label="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {/* iter86.99 — Account sheet: username is read-only (operator
                    decision: no self-rename); email is self-service.
                    iter87.0 — one-tap copy for username / invite / personal code. */}
                {copyables.length > 0 && (
                    <div className="mb-4 space-y-1.5">
                        {copyables.map((c) => (
                            <button
                                key={c.key}
                                type="button"
                                data-testid={`account-copy-${c.key}-btn`}
                                onClick={() => copy(c.key, c.value)}
                                className="w-full flex items-center justify-between gap-2 rounded-xl border border-white/[0.08] bg-[#1A1A1A] px-3 py-2.5 hover:border-white/25 transition-colors active:scale-[0.99]"
                            >
                                <span className="text-left min-w-0">
                                    <span className="block text-[9px] uppercase tracking-widest text-zinc-500">{c.label}</span>
                                    <span className="block font-mono font-bold text-sm truncate" data-testid={c.key === "username" ? "account-username" : undefined}>{c.value}</span>
                                </span>
                                <span className="shrink-0 text-zinc-400">
                                    {copiedKey === c.key ? <Check className="w-4 h-4 text-neon" /> : <Copy className="w-4 h-4" />}
                                </span>
                            </button>
                        ))}
                        <p className="text-[10px] text-zinc-600 px-1">Tap to copy. Ask your rivalry admin if your username needs changing.</p>
                    </div>
                )}
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Recovery email</p>
                <p className="text-xs text-zinc-500 mb-2">
                    We'll only use this to send a magic link if you forget your username.
                </p>
                <input
                    data-testid="email-modal-input"
                    type="email"
                    value={value}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="you@email.com"
                    className="brutal-input w-full mb-4"
                    autoFocus
                />
                <button
                    data-testid="email-modal-save-btn"
                    onClick={() => onSave(value)}
                    className="brutal-btn-primary w-full"
                >
                    Save email <Check className="w-4 h-4" />
                </button>
                {/* iter87.342 — self-serve, per-rivalry email opt-out. */}
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                    <span className="min-w-0">
                        <span className="block text-[10px] uppercase tracking-widest text-zinc-500">Email notifications</span>
                        <span className="block text-xs text-zinc-400">Updates posted by your rivalry admin</span>
                    </span>
                    <button
                        type="button"
                        data-testid="notifications-toggle-btn"
                        onClick={handleToggleNotifs}
                        disabled={notifBusy}
                        aria-pressed={notifsOn}
                        className={`shrink-0 w-11 h-6 rounded-full border-2 border-black transition-colors relative disabled:opacity-50 ${notifsOn ? "bg-cyber" : "bg-white/10"}`}
                    >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform ${notifsOn ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                </div>
            </div>
        </div>
    );
}


export function LogoutConfirmModal({ open, onClose, onConfirm, league, me, onAddEmail }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="brutal-card p-6 max-w-md w-full"
                data-testid="logout-confirm-modal"
            >
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 bg-heat border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <LogOut className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase">Sign out?</h3>
                </div>
                <p className="text-sm text-zinc-300 mb-4">To get back in, you'll need these:</p>
                <div className="space-y-2 mb-4">
                    <div className="brutal-card-light p-3">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Invite code</p>
                        <p className="font-mono font-black text-xl text-cyber tracking-[0.2em]">{league.invite_code}</p>
                    </div>
                    {me?.personal_code && (
                        <div className="brutal-card-light p-3" data-testid="logout-personal-code-card">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Personal code</p>
                            <p className="font-mono font-black text-xl text-blaze tracking-[0.2em]">{me.personal_code}</p>
                        </div>
                    )}
                    <div className="brutal-card-light p-3">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Your username</p>
                        <p className="font-mono font-bold text-lg">{me?.username}</p>
                    </div>
                    {!me?.email && (
                        <p className="text-xs text-zinc-500 leading-relaxed">
                            No recovery email saved. If you forget your username, only your rivalry admin can help you.
                            <button
                                onClick={onAddEmail}
                                className="text-cyber hover:underline ml-1"
                                data-testid="logout-add-email-link"
                            >
                                Add one now
                            </button>
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button onClick={onClose} className="brutal-btn-ghost flex-1" data-testid="logout-cancel-btn">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="brutal-btn-primary flex-1" data-testid="logout-confirm-btn">
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}
